import { Button, FormHelperText, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import FormFooter from './FormFooter';
import FormHeader from './FormHeader';
import FormNavigation from './FormNavigation';
import {
  initialCredentials,
  initialErrors,
  actionPrimary,
  actionSecondary,
  inputPrimary,
  inputSecondary,
  Views,
  otpInput,
} from './LoginForm.const';
import {
  AltLoginContainer,
  AltLoginDivider,
  Container,
  ContentContainer,
  InputWrapper,
  OtpInputWrapper,
} from './LoginForm.styles';
import LoginImage from './LoginImage';
import { Authenticator } from 'services/authenticator';
import { loginErrorHandler } from 'services/api/authenticator';
import { isPostAuthPageAccessed } from './Login.helpers';

type LoginProps = {
  isModal?: boolean;
  isMobile?: boolean;
};

const LoginForm = React.forwardRef<HTMLElement, LoginProps>((props, ref) => {
  const { isModal, isMobile } = props;
  const [state, setState] = useState({
    view: Views.otp_email,
    credentials: initialCredentials,
    errors: initialErrors,
    isLoading: false,
    medium: '',
    requestId: '',
    phone: '',
  });

  const isOtpEmail = state.view === Views.otp_email;
  const isOtpCode = state.view === Views.otp_code;
  const isClassic = state.view === Views.classic;
  const isForgotPass = state.view === Views.forgot_password;

  const handleBack = () => {
    setState((prevState) => ({
      ...prevState,
      view: Views.otp_email,
      errors: initialErrors,
    }));
    return;
  };

  const handleError = (error: { error: boolean; errorPrimary: string; errorSecondary: string }) => {
    setState((prevState) => ({
      ...prevState,
      errors: {
        errorPrimary: error.errorPrimary,
        errorSecondary: error.errorSecondary,
      },
      isLoading: false,
    }));
  };

  const setLoading = () => {
    setState((prevState) => ({ ...prevState, isLoading: true }));
  };

  const actionAfterLogin = () => {
    const { pathname } = window.location;
    const shouldRefresh = isPostAuthPageAccessed(pathname);
    if (shouldRefresh) {
      window.location.replace(pathname);
      return;
    }

    window.location.replace(process.env.PUBLIC_URL);
  };

  const onPrimaryClick = async () => {
    const { credentials, medium, view } = state;

    try {
      setLoading();

      if (view === Views.otp_email) {
        const response = await Authenticator.oneTimePassword(credentials.email, 'email');

        setState((prevState) => ({
          ...prevState,
          isLoading: false,
          view: Views.otp_code,
          medium: 'email',
          requestId: response.request_id,
          errors: initialErrors,
        }));

        return;
      }

      if (isOtpCode && ['email', 'sms'].includes(medium)) {
        await Authenticator.otpLogin(
          credentials.email,
          credentials.code.join(''),
          medium as 'email',
        );

        actionAfterLogin();
        return;
      }

      if (isClassic) {
        await Authenticator.login(credentials.email, credentials.password);

        actionAfterLogin();
        return;
      }

      if (isForgotPass) {
        await Authenticator.forgotPassword(credentials.email);

        setState((prevState) => ({
          ...prevState,
          view: Views.forgot_password_confirmed,
        }));
        return;
      }
    } catch (error) {
      handleError(loginErrorHandler(error));
    }
  };

  const onSecondaryClick = async () => {
    const { credentials } = state;
    try {
      if (isOtpEmail) {
        setLoading();
        const response = await Authenticator.oneTimePassword(credentials.email, 'sms');

        setState((prevState) => ({
          ...prevState,
          isLoading: false,
          view: Views.otp_code,
          medium: 'sms',
          phone: `***-***-${response.phone}`,
          requestId: response.request_id,
          errors: initialErrors,
        }));

        return;
      }

      if (isOtpCode) {
        setState((prevState) => ({
          ...prevState,
          isLoading: true,
          credentials: {
            ...prevState.credentials,
            code: ['', '', '', ''],
          },
          errors: initialErrors,
        }));
        const { medium = 'email' } = state;
        const response = await Authenticator.oneTimePassword(
          credentials.email,
          medium as 'email' | 'sms',
        );

        setState((prevState) => ({
          ...prevState,
          credentials: {
            ...prevState.credentials,
            code: ['', '', '', ''],
          },
          isLoading: false,
          requestId: response.request_id,
        }));
      }

      if (isClassic) {
        setState((prevState) => ({
          ...prevState,
          errors: initialErrors,
          view: Views.forgot_password,
          isLoading: false,
        }));
      }
    } catch (error) {
      handleError(loginErrorHandler(error));
    }
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;
    setState((prevState) => ({
      ...prevState,
      errors: initialErrors,
      credentials: { ...prevState.credentials, [name]: value },
    }));
  };

  const focusNextOtp = (dir: 1 | -1 | 0 = 1) => {
    try {
      const inputs = Array.from(document.querySelectorAll('input[name="otp"]'));
      const focused = document.activeElement as HTMLInputElement;

      if (dir === 0) {
        focused.blur();
        return;
      }

      const focusedIndex = inputs.indexOf(focused);

      const nextElement = inputs[focusedIndex + dir] as HTMLInputElement;

      if (!nextElement) {
        return;
      }

      if (nextElement) {
        nextElement.focus();
        return;
      }

      focused.blur();
    } catch (error) {
      // noop
    }
  };

  const onOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.currentTarget;
    const [, index] = id.split('-');

    if (!/\d/.test(value) && value !== '') {
      return;
    }
    if (value.length > 1) {
      return;
    }

    setState((prevState) => ({
      ...prevState,
      credentials: {
        ...prevState.credentials,
        code: prevState.credentials.code.map((digit, i) => (i === Number(index) ? value : digit)),
      },
      errors: initialErrors,
    }));
  };

  const onOtpKeyup = (e: React.KeyboardEvent) => {
    if (['Backspace', 'ArrowLeft'].includes(e.key)) {
      focusNextOtp(-1);
    }
    if (['ArrowRight'].includes(e.key) || /\d/.test(e.key)) {
      focusNextOtp();
    }
  };

  const onOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const value = e.clipboardData.getData('text');
    if (/\d{4}/.test(value)) {
      setState((prevState) => ({
        ...prevState,
        credentials: {
          ...prevState.credentials,
          code: value.split(''),
        },
      }));
      focusNextOtp(0);
    }
  };

  const onClassicLoginClick = () => {
    setState((prevState) => ({
      ...prevState,
      errors: initialErrors,
      isLoading: false,
      view: Views.classic,
    }));
  };

  const isPrimaryDisabled = () => {
    const { credentials, view, isLoading } = state;

    if (isOtpEmail) {
      return false;
    }

    if (isClassic && (!credentials.email || !credentials.password)) {
      return true;
    }

    if (isForgotPass && !credentials.email) {
      return true;
    }

    if (isOtpCode && credentials.code.some((digit) => digit === '')) {
      return true;
    }

    return isLoading;
  };

  const isSecondaryDisabled = () => {
    const { credentials, view, isLoading } = state;

    if (isOtpEmail && !credentials.email) {
      return true;
    }

    return isLoading;
  };

  useEffect(() => {
    if (state.view === Views.otp_email) {
      setState({
        view: Views.otp_email,
        credentials: initialCredentials,
        errors: initialErrors,
        isLoading: false,
        medium: '',
        requestId: '',
        phone: '',
      });
    }
  }, [state.view]);

  return (
    <Container
      ref={ref as any}
      isAbsolute={isModal && !isMobile}
      isStandalone={!isModal && !isMobile}
    >
      <LoginImage />
      <ContentContainer>
        <FormNavigation
          isModal={isModal}
          canGoBack={state.view !== Views.otp_email}
          onBack={handleBack}
        />
        <FormHeader
          view={state.view}
          confirmationMethod={state.medium === 'sms' ? state.phone : state.credentials.email}
          email={state.credentials.email}
        />
        <InputWrapper
          onSubmit={(e) => {
            e.preventDefault();
            onPrimaryClick();
          }}
        >
          {inputPrimary[state.view] && (
            <TextField
              autoFocus
              variant="outlined"
              onChange={onInputChange}
              value={state.credentials.email}
              inputProps={{ ...inputPrimary[state.view] }}
              label={inputPrimary[state.view]?.label}
              error={!!state.errors.errorPrimary || !!state.errors.errorSecondary}
              helperText={state.errors.errorPrimary}
            />
          )}
          {inputSecondary[state.view] && (
            <TextField
              variant="outlined"
              onChange={onInputChange}
              value={state.credentials.password}
              inputProps={{ ...inputSecondary[state.view] }}
              label={inputSecondary[state.view]?.label}
              error={!!state.errors.errorSecondary}
              helperText={state.errors.errorSecondary}
            />
          )}
          {isOtpCode && (
            <OtpInputWrapper>
              {state.credentials.code.map((value, i) => (
                <TextField
                  key={i}
                  id={`otp-${i}`}
                  variant="outlined"
                  name={'otp'}
                  autoFocus={i === 0}
                  onChange={onOtpChange}
                  onPaste={onOtpPaste}
                  onKeyUp={onOtpKeyup}
                  onFocus={(e) => {
                    e.currentTarget.select();
                  }}
                  value={value}
                  inputProps={{ ...otpInput }}
                  error={!!state.errors.errorPrimary}
                />
              ))}
              {state.errors.errorPrimary && (
                <FormHelperText error>{state.errors.errorPrimary}</FormHelperText>
              )}
            </OtpInputWrapper>
          )}
          {actionPrimary[state.view] && (
            <Button
              variant="contained"
              onClick={(e) => {
                e.preventDefault();
                onPrimaryClick();
              }}
              type="submit"
              disabled={isPrimaryDisabled()}
            >
              {actionPrimary[state.view]}
            </Button>
          )}
          {actionSecondary[state.view] && (
            <Button
              variant="text"
              onClick={onSecondaryClick}
              disabled={isSecondaryDisabled()}
            >
              {actionSecondary[state.view]}
            </Button>
          )}
        </InputWrapper>
        {isOtpEmail && (
          <AltLoginContainer>
            <AltLoginDivider>OR</AltLoginDivider>
            <Button
              variant="outlined"
              onClick={onClassicLoginClick}
            >
              By email and password
            </Button>
          </AltLoginContainer>
        )}
        <FormFooter view={state.view} />
      </ContentContainer>
    </Container>
  );
});

export default LoginForm;
